import { CloudProvider, PipelineMode, InfraType, StackType } from "../../types";

import { P } from "./wizard-constants";
import { WizardState } from "./wizard-types";

export function parseFromParams(params: URLSearchParams): {
  step: number;
  state: WizardState;
} {
  const step = Math.min(6, Math.max(0, Number(params.get(P.STEP) ?? "0")));
  return {
    step,
    state: {
      stackType: (params.get(P.TYPE) as StackType | null) ?? null,
      cloudProvider: (params.get(P.CLOUD) as CloudProvider | null) ?? null,
      infraType: (params.get(P.INFRA) as InfraType | null) ?? null,
      // Full objects re-resolved from server data by child steps
      infraTarget: null,
      targetStepDone: params.get(P.TARGET_DONE) === "true",
      selectedTemplate: null,
      configuredServices: [], // Loaded from template when selected
      pipelineMode:
        (params.get(P.PIPELINE_MODE) as PipelineMode | null) ??
        PipelineMode.RUSHA_MANAGED,
      name: decodeURIComponent(params.get(P.NAME) ?? ""),
      region: decodeURIComponent(params.get(P.REGION) ?? ""),
      enableObservability: params.get("obs") === "true",
    },
  };
}

export function serializeToParams(
  step: number,
  state: WizardState,
): Record<string, string> {
  const params: Record<string, string> = { [P.STEP]: String(step) };
  if (state.stackType) params[P.TYPE] = state.stackType;
  if (state.cloudProvider) params[P.CLOUD] = state.cloudProvider;
  if (state.infraType) params[P.INFRA] = state.infraType;
  if (state.targetStepDone) params[P.TARGET_DONE] = "true";
  if (state.infraTarget) params[P.TARGET_ID] = String(state.infraTarget.id);
  if (state.selectedTemplate) params[P.TEMPLATE] = state.selectedTemplate.slug;
  if (state.configuredServices.length > 0) {
    params[P.SERVICES] = "configured"; // Simple flag indicating services were configured
  }
  if (state.pipelineMode !== PipelineMode.RUSHA_MANAGED) {
    params[P.PIPELINE_MODE] = state.pipelineMode;
  }
  if (state.name.trim()) params[P.NAME] = state.name;
  if (state.region) params[P.REGION] = state.region;
  return params;
}

// ── Validation per step ───────────────────────────────────────────────────────

export function isStepValid(step: number, state: WizardState): boolean {
  if (step === 0) return state.stackType !== null;
  if (step === 1)
    return state.cloudProvider !== null && state.infraType !== null;
  // Step 2: valid if rusha_hosted (no target needed), or user selected/skipped a target
  if (step === 2)
    return state.infraType === "rusha_hosted" || state.targetStepDone;
  if (step === 3) return state.selectedTemplate !== null;
  // Step 4: Services are optional - valid if template is selected
  if (step === 4) return state.selectedTemplate !== null;
  // Step 5: Build/deploy mode — always valid since default is set
  if (step === 5) return state.pipelineMode !== null;
  // Step 6: Name and region required
  if (step === 6)
    return state.name.trim().length > 0 && state.region.length > 0;
  return false;
}

export function toAlias(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
